/**
@format
@flow local-strict
*/

import React from 'react';
import styled from 'styled-components';
import { colors } from '@ziynx/theme';
import { getDimensions } from '@ziynx/components/utilities';

export type Props = $ReadOnly<{|
  /**
   * If listItem should be disabled
   */
  disabled?: boolean,
  /**
   * Depicts when ListItem is active
   */
  isActive?: boolean,
  /**
   * Depicts when ListItem is hovered
   */
  isHovered?: boolean,
  /**
   * Different default sizing for small and medium styles
   * Default medium
   */
  size?: 'small' | 'medium' | 'large',
  /**
   * Children for ListItem
   */
  children?: any,
   /**
   * Text to be rendered if passed
   */
  text?: string,
  /**
   * Color for text
   * Default GrayVeryDark
   */
  textColor?: string,
   /**
   * Width measurements
   * Default 100%
   */
  width?: string | number,
  /**
   * MaxWidth measurements
   */
  maxWidth?: string | number,
  /**
   * Padding top measurement
   */
  paddingTop?: string,
  /**
   * Padding bottom measurement
   */
  paddingBottom?: string,
   /**
   * Padding left measurement
   */
  paddingLeft?: string,
  /**
   * Padding bottom measurement
  */
  paddingRight?: string,
  /**
   * Custom transition
  */
  transition?: string,
  /**
   * Child horizontal alignment
   */
  alignment?: 'flex-start' | 'center' | 'flex-end' | 'space-between',
  /**
   * List Item Wrapper bg color
   */
  backgroundColor?: string,
  /**
   * List Item Wrapper border radius
   */
  borderRadius?: string,
  /**
  * Custom color set
  */
  colorConfig?: Object,
  /**
   * Label prop for other variations
   */
  label?: {
    text?: string,
    labelPosition?: 'bottom' | 'top',
    color?: string,
  }
|}>;

/**
 * Function to determine width or maxWidth
 */
const findWidth = (size, maxWidth) => {
  const maxWidthFormat = getDimensions(maxWidth);
  switch (size) {
  case 'small':
    return maxWidthFormat || '296px';

  case 'medium':
  default:
    return maxWidthFormat || '656px';
  }
};

const findBackgroundColor = (disabled, selected, isHovered) => {
  if (!disabled && (selected || isHovered)) {
    return colors.BrandBlueFaded;
  }
  return colors.White;
};

const _getTextAlignment = (alignment) => {
  if (alignment === 'middle' || alignment === 'space-between') return 'center';
  if (alignment === 'flex-end') return 'right';
  return 'left';
};

const BaseWrapper = styled.span`
  display: flex;
  box-sizing: border-box;
  align-items: ${({ alignment }) => alignment};
  text-align: ${({ alignment }) => _getTextAlignment(alignment)};
  width: ${({ width }) => getDimensions(width)};
  max-width: ${({ maxWidth, size }) => findWidth(size, maxWidth)};
  padding-top: ${({ paddingTop }) => paddingTop};
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
  padding-left: ${({ paddingLeft }) => paddingLeft};
  padding-right: ${({ paddingRight }) => paddingRight};
  ${({ transition }) => transition && `transition: ${transition};`};
  background-color: ${({ backgroundColor }) => backgroundColor};
  ${({ borderRadius }) => borderRadius && `border-radius: ${borderRadius};`};
`;

/**
 * Base for ListItem variants
 */
const ListItemWrapper = (props: Props): React$Node => {
  const {
    children,
    text,
    disabled,
    isActive,
    isHovered,
    backgroundColor,
    colorConfig,
    ...rest
  } = props;

  // to do clone children and pass isHovering
  return (
    <BaseWrapper
      backgroundColor={
        backgroundColor || findBackgroundColor(disabled, isActive, isHovered)
      }
      colorConfig={colorConfig}
      {...rest}
    >
      {children || text}
    </BaseWrapper>
  );
};

ListItemWrapper.defaultProps = {
  isHovered: false,
  width: '100%',
  textColor: colors.GrayVeryDark,
  alignment: 'flex-start',
  paddingTop: '0px',
  paddingBottom: '0px',
  paddingLeft: '0px',
  paddingRight: '0px',
  size: 'medium',
  colorConfig: colors,
};

export default ListItemWrapper;
